// App Auth Actions
import ACTIONS from "@/Constants/actionsList";

// Interfaces
import { IPayPlansServices } from "./interfaces";

// Helpers
import { getDepartmentalPosition, isMutablePosition, supportedSystemPositions } from "@/Common/helpers/Positions";

// Internal (Positions) Services
import { getServiceDepartmentalServices } from "./managers/departmental";
import { getSalesDepartmentalServices } from "./managers/sales";
import { getFinanceServices } from "./finance";
import { getTechniciansServices } from "./technicians";
import { getAdvisorsServices } from "./advisors";

// API's
// Sales Associates Queries
import {
	requestSalesPayPlanMatches,
	retrieveSalesPp,
	retrieveSalesPpList,
	getSalesPayPlanSidebarTree,
} from "@/PayPlans/api/sales/associates/queries";
// Sales Associates Mutations
import {
	addSalesPayPlan,
	cloneSalesPayPlan,
	deleteSalesPayPlan,
	saveNewSalesPayPlan,
	updateSalesPayPlan,
	updateSalesAvailablePayPlan,
} from "@/PayPlans/api/sales/associates/mutations";
// Sales Associates Subscriptions
import {
	onCloneSalesPayPlan,
	onCreateSalesPayPlan,
	onDeleteSalesPayPlan,
	onUpdateSalesPayPlan,
	onUpdateAnySalesPayPlan,
	onUpdateSalesAvailablePayPlan,
} from "@/PayPlans/api/sales/associates/subscriptions";

const POSITIONS = supportedSystemPositions();

const declare = () => {
	// Auth actions
	const actions = {
		editable: "",
	};

	// API Queries
	const queries = {
		getPayPlan: async () => {},
		getPayPlans: async () => {},
		getPayPlansSidebarTree: async () => {},
		getMatches: async () => {},
	};

	// API Mutations
	const mutations = {
		addPayPlan: async () => {},
		saveNewPayPlan: async () => {},
		updatePayPlan: async () => {},
		deletePayPlan: async () => {},
		clonePayPlan: async () => {},
		updateAvailablePayPlan: async () => {},
	};

	// API Subscriptions
	const subscriptions = new Map([
		["onClonePayPlan", { query: "", getter: "" }],
		["onCreatePayPlan", { query: "", getter: "" }],
		["onDeletePayPlan", { query: "", getter: "" }],
		["onUpdatePayPlan", { query: "", getter: "" }],
		["onUpdateAnyPayPlan", { query: "", getter: "" }],
		["onUpdateAvailablePayPlan", { query: "", getter: "" }],
	]);

	return {
		actions,
		queries,
		mutations,
		subscriptions,
	};
};

const getDepartmentalServices = (position: string): IPayPlansServices => {
	switch (position) {
		case POSITIONS.FINANCE.FINANCE_DIRECTOR:
			return getSalesDepartmentalServices(position);
		case POSITIONS.PARTS.PARTS_COUNTER_ASSOCIATE:
			return getServiceDepartmentalServices(position);
		default:
			break;
	}
	return declare();
};

const getSalesAssociateServices = (): IPayPlansServices => {
	// Auth actions
	const actions = {
		editable: ACTIONS.EDIT_SALES_ASSOCIATE_PAY_PLANS,
	};

	// API Queries
	const queries = {
		getPayPlan: retrieveSalesPp,
		getPayPlans: retrieveSalesPpList,
		getPayPlansSidebarTree: getSalesPayPlanSidebarTree,
		getMatches: requestSalesPayPlanMatches,
	};

	// API Mutations
	const mutations = {
		addPayPlan: addSalesPayPlan,
		saveNewPayPlan: saveNewSalesPayPlan,
		updatePayPlan: updateSalesPayPlan,
		deletePayPlan: deleteSalesPayPlan,
		clonePayPlan: cloneSalesPayPlan,
		updateAvailablePayPlan: updateSalesAvailablePayPlan,
	};

	// API Subscriptions
	const subscriptions = new Map([
		["onClonePayPlan", { query: onCloneSalesPayPlan, getter: "onCloneSalesPayPlan" }],
		["onCreatePayPlan", { query: onCreateSalesPayPlan, getter: "onCreateSalesPayPlan" }],
		["onDeletePayPlan", { query: onDeleteSalesPayPlan, getter: "onDeleteSalesPayPlan" }],
		["onUpdatePayPlan", { query: onUpdateSalesPayPlan, getter: "onUpdateSalesPayPlan" }],
		["onUpdateAnyPayPlan", { query: onUpdateAnySalesPayPlan, getter: "onUpdateAnySalesPayPlan" }],
		["onUpdateAvailablePayPlan", { query: onUpdateSalesAvailablePayPlan, getter: "onUpdateSalesAvailablePayPlan" }],
	]);

	return {
		actions,
		queries,
		mutations,
		subscriptions,
	};
};

const getFinanceManagerServices = (): IPayPlansServices => getFinanceServices(POSITIONS.FINANCE.FINANCE_MANAGER);

// Relative to Producer
const getFinanceDirectorServices = (): IPayPlansServices => getFinanceServices(POSITIONS.FINANCE.FINANCE_DIRECTOR);

const REST_OF_SERVICES = new Map<string, Function>([
	[POSITIONS.BODY_SHOP.BODY_SHOP_TECHNICIAN, getTechniciansServices],
	[POSITIONS.BODY_SHOP.BODY_SHOP_PAINTER, getTechniciansServices],
	[POSITIONS.SERVICE.SERVICE_TECHNICIAN, getTechniciansServices],
	[POSITIONS.SALES.SALES_MANAGER, getSalesDepartmentalServices],
	[POSITIONS.SALES.GENERAL_SALES_MANAGER, getSalesDepartmentalServices],
	[POSITIONS.SERVICE.SERVICE_MANAGER, getServiceDepartmentalServices],
	[POSITIONS.SERVICE.SERVICE_DIRECTOR, getServiceDepartmentalServices],
	[POSITIONS.SERVICE.SERVICE_FOREMAN, getServiceDepartmentalServices],
	[POSITIONS.PARTS.PARTS_MANAGER, getServiceDepartmentalServices],
	[POSITIONS.PARTS.PARTS_DIRECTOR, getServiceDepartmentalServices],
	[POSITIONS.BODY_SHOP.BODY_SHOP_MANAGER, getServiceDepartmentalServices],
	[POSITIONS.BODY_SHOP.BODY_SHOP_PRODUCT_MANAGER, getServiceDepartmentalServices],
	[POSITIONS.BODY_SHOP.BODY_SHOP_FOREMAN, getServiceDepartmentalServices],
	[POSITIONS.SERVICE.SERVICE_ADVISOR, getAdvisorsServices],
	[POSITIONS.SERVICE.INTERNAL_SERVICE_ADVISOR, getAdvisorsServices],
	[POSITIONS.BODY_SHOP.BODY_SHOP_ESTIMATOR, getAdvisorsServices],
]);

const POSITIONS_SERVICES = new Map([
	[POSITIONS.SALES.SALES_ASSOCIATE, getSalesAssociateServices()],
	[POSITIONS.FINANCE.FINANCE_MANAGER, getFinanceManagerServices()],
	[POSITIONS.FINANCE.FINANCE_DIRECTOR, getFinanceDirectorServices()],
]);

const getRegularServices = (position: string): IPayPlansServices => {
	const service = REST_OF_SERVICES.get(position);
	if (service) {
		return service(position);
	}
	return POSITIONS_SERVICES.get(position) ?? declare();
};

const useServices = (position: string, positionType = ""): IPayPlansServices => {
	const departmental = getDepartmentalPosition(position);
	const isMutable = isMutablePosition(departmental);
	const normalizedType = positionType.toUpperCase();

	if (isMutable && normalizedType === "DEPARTMENTAL") {
		return getDepartmentalServices(departmental);
	}

	return getRegularServices(departmental);
};

export { useServices };
